import type { ColumnFiltersState } from '@tanstack/react-table';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import type { RoyaltyCheckLineItemsSearchParams } from './types';

export const DEFAULT_LINE_ITEM_PARAMS: RoyaltyCheckLineItemsSearchParams = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

// Table column id -> backend sortAttribute. Every value must be a key in
// Query::RoyaltyCheckLineItems::SORT_ATTRIBUTE_MAP; the mapping is identity here
// because the column ids were chosen to match. Columns absent from this map fall
// back to the column id (and would be rejected by the query's validation).
export const LINE_ITEM_SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  propertyName: 'propertyName',
  wellName: 'wellName',
  product: 'product',
  productCategory: 'productCategory',
  interestType: 'interestType',
  lineItemDecimal: 'lineItemDecimal',
  price: 'price',
  grossVolume: 'grossVolume',
  grossValue: 'grossValue',
  grossAdjustment: 'grossAdjustment',
  grossNet: 'grossNet',
  lineItemNetValue: 'lineItemNetValue',
  ownerVolume: 'ownerVolume',
  ownerValue: 'ownerValue',
  ownerTaxes: 'ownerTaxes',
  ownerAdjustment: 'ownerAdjustment',
  adjustmentCode: 'adjustmentCode',
  productionMonth: 'productionMonth',
};

// Reset every filter-backed param each pass, so clearing a column filter clears
// its param (rather than leaving a stale value in the search payload).
const COLUMN_FILTER_PARAM_KEYS: Array<keyof RoyaltyCheckLineItemsSearchParams> =
  [
    'name',
    'propertyName',
    'wellName',
    'product',
    'productCategory',
    'interestType',
    'lineItemDecimalMin',
    'lineItemDecimalMax',
    'priceMin',
    'priceMax',
    'grossVolumeMin',
    'grossVolumeMax',
    'grossValueMin',
    'grossValueMax',
    'grossAdjustmentMin',
    'grossAdjustmentMax',
    'lineItemNetValueMin',
    'lineItemNetValueMax',
    'ownerVolumeMin',
    'ownerVolumeMax',
    'ownerValueMin',
    'ownerValueMax',
    'ownerTaxesMin',
    'ownerTaxesMax',
    'ownerAdjustmentMin',
    'ownerAdjustmentMax',
    'productionMonthStart',
    'productionMonthEnd',
  ];

const readString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const readNumericRange = (value: unknown): { min?: number; max?: number } => {
  if (!Array.isArray(value) || value.length !== 2) return {};
  const [rawMin, rawMax] = value as [unknown, unknown];
  const min =
    typeof rawMin === 'number' && !Number.isNaN(rawMin) ? rawMin : undefined;
  const max =
    typeof rawMax === 'number' && !Number.isNaN(rawMax) ? rawMax : undefined;
  return { min, max };
};

const readDateRange = (value: unknown): { start?: string; end?: string } => {
  if (!Array.isArray(value) || value.length !== 2) return {};
  const [rawStart, rawEnd] = value as [unknown, unknown];
  return {
    start: typeof rawStart === 'string' && rawStart ? rawStart : undefined,
    end: typeof rawEnd === 'string' && rawEnd ? rawEnd : undefined,
  };
};

// Numeric-range column id -> [minParam, maxParam].
const NUMERIC_RANGE_COLUMNS: Record<
  string,
  [
    keyof RoyaltyCheckLineItemsSearchParams,
    keyof RoyaltyCheckLineItemsSearchParams,
  ]
> = {
  lineItemDecimal: ['lineItemDecimalMin', 'lineItemDecimalMax'],
  price: ['priceMin', 'priceMax'],
  grossVolume: ['grossVolumeMin', 'grossVolumeMax'],
  grossValue: ['grossValueMin', 'grossValueMax'],
  grossAdjustment: ['grossAdjustmentMin', 'grossAdjustmentMax'],
  lineItemNetValue: ['lineItemNetValueMin', 'lineItemNetValueMax'],
  ownerVolume: ['ownerVolumeMin', 'ownerVolumeMax'],
  ownerValue: ['ownerValueMin', 'ownerValueMax'],
  ownerTaxes: ['ownerTaxesMin', 'ownerTaxesMax'],
  ownerAdjustment: ['ownerAdjustmentMin', 'ownerAdjustmentMax'],
};

// Text "contains" column id -> param.
const TEXT_COLUMNS: Record<string, keyof RoyaltyCheckLineItemsSearchParams> = {
  name: 'name',
  propertyName: 'propertyName',
  wellName: 'wellName',
  product: 'product',
  productCategory: 'productCategory',
  interestType: 'interestType',
};

export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<RoyaltyCheckLineItemsSearchParams> => {
  const params: Partial<RoyaltyCheckLineItemsSearchParams> = {};
  for (const key of COLUMN_FILTER_PARAM_KEYS) {
    (params as Record<string, unknown>)[key] = undefined;
  }

  for (const filter of filters) {
    const { id } = filter;

    if (id in TEXT_COLUMNS) {
      (params as Record<string, unknown>)[TEXT_COLUMNS[id]] = readString(
        filter.value
      );
      continue;
    }

    if (id in NUMERIC_RANGE_COLUMNS) {
      const [minKey, maxKey] = NUMERIC_RANGE_COLUMNS[id];
      const { min, max } = readNumericRange(filter.value);
      (params as Record<string, unknown>)[minKey] = min;
      (params as Record<string, unknown>)[maxKey] = max;
      continue;
    }

    if (id === 'productionMonth') {
      const { start, end } = readDateRange(filter.value);
      params.productionMonthStart = start;
      params.productionMonthEnd = end;
    }
  }

  return params;
};
